import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import {
	ArrowRight,
	CheckCircle,
	CreditCard,
	RotateCw,
	Settings,
	Upload,
	Users,
} from 'lucide-react';
import { useSubscription } from 'src/hooks';

import {
	useCompleteOnboardingMutation,
	useGetDetailedDocumentQuery,
} from '../../api';
import { useConfigs } from '../../hooks/useConfigs';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useAuth } from '../../providers/AppAuthProvider';
import { Button } from '../common';

const Onboarding: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const {
		onboardingState,
		checkAndStartOnboarding,
		nextStep,
		completeOnboarding,
		isStepCompleted,
		currentStepId,
		steps: reduxSteps,
		uploadedFileId,
		completeCurrentStep,
	} = useOnboarding();

	const { subscriptionStatus, onboardingCompleted } = useSubscription();
	const { configs } = useConfigs();
	const [completeOnboardingMutation] = useCompleteOnboardingMutation();
	const [isCompletingOnboarding, setIsCompletingOnboarding] = useState(false);

	// Reset completion state when onboarding is actually completed
	useEffect(() => {
		if (onboardingCompleted) {
			setIsCompletingOnboarding(false);
		}
	}, [onboardingCompleted]);

	// Check file processing status when we have an uploadedFileId
	const { data: documentData, refetch: refetchDocument } =
		useGetDetailedDocumentQuery(uploadedFileId || '', {
			refetchOnMountOrArgChange: true,
			skip: !uploadedFileId, // Skip the query if no file ID
		});

	// Get the current processing status
	const currentProcessingStatus = documentData?.current_status || 'unknown';

	// Extract values from context
	const { isOnboarding } = onboardingState;

	// Start onboarding when component mounts if not already started
	useEffect(() => {
		if (!isOnboarding && user?.email) {
			// Use checkAndStartOnboarding to avoid navigation
			checkAndStartOnboarding();
		}
	}, [isOnboarding, user?.email, checkAndStartOnboarding]);

	useEffect(() => {
		if (window.location.pathname !== '/onboarding') return;

		const allStepsCompleted =
			reduxSteps.length > 0 && reduxSteps.every((step) => step.completed);

		// Check if all steps are completed - redirect to home
		if (allStepsCompleted && !onboardingCompleted && !isCompletingOnboarding) {
			setIsCompletingOnboarding(true);

			// Mark onboarding as complete in backend
			completeOnboardingMutation()
				.catch((err) => console.error('Failed to complete onboarding:', err))
				.finally(() => {
					setTimeout(() => navigate('/'), 800);
				});
			return;
		}

		// Check if current step is completed and should auto-advance
		if (
			isStepCompleted(currentStepId) &&
			currentStepId < reduxSteps.length &&
			!isStepCompleted(currentStepId + 1)
		) {
			const timer = setTimeout(() => {
				if (
					window.location.pathname === '/onboarding' &&
					isStepCompleted(currentStepId) &&
					currentStepId !== 1
				) {
					nextStep();
				}
			}, 1000);

			return () => clearTimeout(timer);
		}
	}, [
		currentStepId,
		reduxSteps,
		nextStep,
		completeOnboardingMutation,
		navigate,
		isStepCompleted,
		onboardingCompleted,
	]);

	const steps = reduxSteps.map((step) => ({
		id: step.id,
		title: step.name,
		description: step.description,
		icon:
			step.id === 1 ? (
				<Upload className="w-6 h-6" />
			) : step.id === 2 ? (
				<CreditCard className="w-6 h-6" />
			) : step.id === 3 ? (
				<Settings className="w-6 h-6" />
			) : (
				<Users className="w-6 h-6" />
			),
		status: step.completed
			? 'completed'
			: step.id === currentStepId
				? 'current'
				: 'upcoming',
	}));

	const getStepStatus = (stepId: number) => {
		if (isStepCompleted(stepId)) return 'completed';
		if (stepId === currentStepId) return 'current';
		return 'upcoming';
	};

	const renderStepContent = () => {
		switch (currentStepId) {
			case 1:
				if (isStepCompleted(1) && uploadedFileId) {
					const renderActions = () => {
						return (
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => {
										if (uploadedFileId) {
											navigate(`/onboarding/analysis/${uploadedFileId}`);
										} else {
											console.error('No uploadedFileId available');
											navigate('/onboarding/upload');
										}
									}}
									variant="primary"
									size="lg"
								>
									<span>View Analysis</span>
								</Button>
								<Button
									onClick={() => nextStep()}
									variant="secondary"
									size="lg"
								>
									Continue to Next Step
								</Button>
							</div>
						);
					};
					// Check the actual processing status to show appropriate message
					if (currentProcessingStatus === 'completed') {
						// File has been processed and analyzed
						return (
							<div className="text-center">
								<div className="mb-8">
									<h2 className="text-2xl font-bold text-primary-900 mb-4">
										✅ Statement Analysis Complete!
									</h2>
									<p className="text-sm text-primary-600">
										Your statement has been analyzed successfully. You can view
										the results or continue to the next step.
									</p>
								</div>
								{renderActions()}
							</div>
						);
					} else if (currentProcessingStatus === 'processing') {
						// File is still being processed
						return (
							<div className="text-center">
								<div className="mb-8 flex items-center justify-center">
									<RotateCw className="animate-spin mr-2" />
									<h2 className="text-2xl font-bold text-primary-900">
										Statement Processing...
									</h2>
								</div>
								{renderActions()}
							</div>
						);
					} else if (currentProcessingStatus === 'error') {
						// File processing failed
						return (
							<div className="text-center">
								<div className="mb-8">
									<h2 className="text-2xl font-bold text-primary-900 mb-4">
										❌ Statement Processing Failed
									</h2>
									<p className="text-sm text-primary-600">
										There was an error processing your statement. Please try
										uploading again.
									</p>
								</div>
								<div className="flex justify-center space-x-4">
									<Button
										onClick={() => navigate('/onboarding/upload')}
										variant="danger"
										size="lg"
									>
										<span>Try Again</span>
									</Button>
									<Button
										onClick={() => nextStep()}
										className="text-gray-600 hover:text-gray-800 px-6 py-3 rounded-lg font-medium border border-gray-300 hover:border-gray-400"
									>
										Continue to Next Step
									</Button>
								</div>
							</div>
						);
					} else {
						// Unknown status or still uploading
						return (
							<div className="text-center">
								<div className="mb-8">
									<h2 className="text-2xl font-bold text-yellow-900 mb-4">
										📤 Statement Uploaded
									</h2>
								</div>
								<div className="flex justify-center space-x-4">
									<button
										onClick={() => refetchDocument()}
										className="bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-medium flex items-center space-x-2"
									>
										<span>Check Status Now</span>
									</button>
									<button
										onClick={() => nextStep()}
										className="text-gray-600 hover:text-gray-800 px-6 py-3 rounded-lg font-medium border border-gray-300 hover:border-gray-400"
									>
										Continue to Next Step
									</button>
								</div>
							</div>
						);
					}
				} else {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-4">
									Upload your first statement
								</h2>
								<p className="text-sm text-gray-600">
									We'll start with your first analysis for free.
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => navigate('/onboarding/upload')}
									variant="primary"
									size="lg"
									icon={Upload}
								>
									<span>Let's Go</span>
								</Button>
							</div>
						</div>
					);
				}
			case 2:
				if (isStepCompleted(2) && subscriptionStatus?.credits_total !== 0) {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-green-900 mb-4">
									✅ Credits Purchased Successfully
								</h2>
								<p className="text-sm text-green-600">
									Your credits have been purchased. You can continue to the next
									step.
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => navigate('/onboarding/subscribe')}
									variant="primary"
									size="lg"
									icon={CreditCard}
								>
									<span>View Credits</span>
								</Button>
								<Button
									onClick={() => nextStep()}
									variant="secondary"
									size="lg"
								>
									Continue to Next Step
								</Button>
							</div>
						</div>
					);
				} else {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-4">
									Purchase Credits
								</h2>
								<p className="text-sm text-gray-600">
									Purchase credits to continue analyzing statements with
									advanced features
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => navigate('/onboarding/subscribe')}
									variant="primary"
									size="lg"
									icon={CreditCard}
								>
									<span>Purchase Credits</span>
								</Button>
								<Button
									onClick={() => {
										nextStep();
										completeCurrentStep();
									}}
									variant="secondary"
									size="lg"
								>
									<span>Skip to Next Step</span>
								</Button>
							</div>
						</div>
					);
				}
			case 3:
				if (isStepCompleted(3) && configs.length > 0) {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-green-900 mb-4">
									✅ Rates Configured Successfully
								</h2>
								<p className="text-sm text-green-600">
									Your custom rates have been configured. You can continue to
									the next step.
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => navigate('/onboarding/config/new')}
									variant="primary"
									size="lg"
									icon={Settings}
								>
									<span>View Configuration</span>
								</Button>
								<Button
									onClick={() => {
										nextStep();
										completeCurrentStep();
									}}
									variant="secondary"
									size="lg"
								>
									Continue to Next Step
								</Button>
							</div>
						</div>
					);
				} else {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-4">
									Configure Your Rates
								</h2>
								<p className="text-sm text-gray-600">
									Create custom configuration for accurate statement analysis
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => navigate('/onboarding/config/new')}
									variant="primary"
									size="lg"
									icon={Settings}
								>
									<span>Configure Rates</span>
								</Button>
								<Button
									onClick={() => {
										nextStep();
										completeCurrentStep();
									}}
									variant="secondary"
									size="lg"
								>
									<span>Skip for Now</span>
								</Button>
							</div>
						</div>
					);
				}
			case 4:
				if (isStepCompleted(4)) {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-green-900 mb-4">
									🎉 Onboarding Complete!
								</h2>
								<p className="text-sm text-green-600">
									All steps completed successfully. Redirecting to Analyzer
									Dashboard...
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<Button
									onClick={() => navigate('/onboarding/team')}
									variant="primary"
									size="lg"
									icon={Users}
								>
									<span>View Team</span>
								</Button>
								<Button
									onClick={() => {
										completeOnboarding();
									}}
									variant="primary"
									size="lg"
									icon={ArrowRight}
								>
									<span>Go to Dashboard</span>
								</Button>
							</div>
						</div>
					);
				} else {
					return (
						<div className="text-center">
							<div className="mb-8">
								<h2 className="text-2xl font-bold text-gray-900 mb-4">
									Invite your team to join
								</h2>
								<p className="text-sm text-gray-600">
									Add your teammates so you can collaborate, share reports, and
									track analyses together.
								</p>
							</div>
							<div className="flex justify-center space-x-4">
								<button
									onClick={() => navigate('/onboarding/team')}
									className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium flex items-center space-x-2"
								>
									<Users className="w-5 h-5" />
									<span>Add Team Members</span>
								</button>
								<button
									onClick={async () => {
										await completeOnboardingMutation();
										completeOnboarding();
									}}
									className="text-gray-600 hover:text-gray-800 px-6 py-3 rounded-lg font-medium border border-gray-300 hover:border-gray-400 flex items-center space-x-2"
								>
									<span>Go to Dashboard</span>
								</button>
							</div>
						</div>
					);
				}
			default:
				return null;
		}
	};

	// Don't render if no user email is available
	if (!user?.email) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
					<p className="text-gray-600">Loading user information...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 py-12 pt-24">
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Stepper */}
				<div className="mb-12">
					<div className="flex items-center justify-center space-x-8">
						{steps.map((step, index) => (
							<React.Fragment key={step.id}>
								<div className="flex flex-col items-center">
									<div
										className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
											getStepStatus(step.id) === 'completed'
												? 'bg-primary-500 border-primary-500 text-white'
												: getStepStatus(step.id) === 'current'
													? 'bg-primary-600 border-primary-600 text-white'
													: 'bg-white border-gray-300 text-gray-400'
										}`}
									>
										{getStepStatus(step.id) === 'completed' ? (
											<CheckCircle className="w-6 h-6" />
										) : (
											step.icon
										)}
									</div>
									<div className="mt-2 text-center">
										<div
											className={`text-sm font-medium ${
												getStepStatus(step.id) === 'completed'
													? 'text-primary-600'
													: getStepStatus(step.id) === 'current'
														? 'text-primary-600'
														: 'text-gray-500'
											}`}
										>
											{step.title}
										</div>
										<div className="text-xs text-gray-400 mt-1 max-w-32">
											{/* {step.description} */}
										</div>
									</div>
								</div>
								{index < steps.length - 1 && (
									<div
										className={`w-16 h-0.5 ${
											getStepStatus(step.id) === 'completed'
												? 'bg-primary-500'
												: 'bg-gray-300'
										}`}
									/>
								)}
							</React.Fragment>
						))}
					</div>
				</div>

				{/* Step Content */}
				<div className="bg-white rounded-lg shadow-lg p-8">
					{renderStepContent()}
				</div>
			</div>
		</div>
	);
};

export default Onboarding;
